// src/hooks/useFirmas.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api/axios";
import { enqueueSnackbar } from "../components/utils/snackbar";

/* ══════════════════════════════════════════════════════════════
   Hook: firmas de reserva
   · useFirmas          → GET  /reservas/:id/firmas
   · useFirmarMutation  → POST /reservas/:id/firmas
   Maneja 403 con un mensaje amigable.
   ══════════════════════════════════════════════════════════════ */

/* ─── leer firmas (lista sin PNG) ────────────────────────────── */
export function useFirmas(reservaId?: number) {
  return useQuery({
    queryKey: ["firmas", reservaId],
    enabled: !!reservaId,
    queryFn: () =>
      api.get(`/reservas/${reservaId}/firmas`).then(
        (r) =>
          r.data as {
            idUsuario: number;
            rolUsuario: "profesor" | "alumno";
            creadoEn: string;
          }[]
      ),
  });
}

/* ─── firmar ─────────────────────────────────────────────────── */
export function useFirmarMutation() {
  const qc = useQueryClient();

  return useMutation({
    /* --- petición POST --- */
    mutationFn: ({ id, pngBase64 }: { id: number; pngBase64: string }) =>
      api.post(`/reservas/${id}/firmas`, { firmaBase64: pngBase64 }),

    /* --- éxito --- */
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["firmas", id] }); // refresca lista de firmas
      qc.invalidateQueries({ queryKey: ["reservas"] }); // refresca estado/resumen
      enqueueSnackbar("¡Firma registrada!", { variant: "success" });
    },

    /* --- error --- */
    onError: (err: any) => {
      const status = err?.response?.status;
      const msg =
        status === 403
          ? "No estás autorizado a firmar esta clase"
          : (err?.response?.data?.message ?? err?.message ?? "No se pudo registrar la firma");

      enqueueSnackbar(msg, { variant: "error", autoHideDuration: 6000 });
    },
  });
}
